<template>
  <div class="flex flex-col gap-8">

    <FormGroup :label="$t('forms.device.device_properties_form.serial_number')" :error-text="form.errors?.serial_number">
      <InputText
          v-model="form.serial_number"
          :placeholder="$t('common.example', { example: 'AB23XYZ' })"
          data-cy="device-serial-number"
          @change="form.validate('serial_number')"
          :disabled="disabled"/>
    </FormGroup>

    
    <FormGroup :label="$t('forms.device.device_properties_form.is_third_party')">
      <div class="flex items-center gap-3">
        <InputSwitch
            v-model="form.is_third_party"
            :disabled="disabled"
            @change="onThirdPartyChange"
            data-cy="third-party-switch"
        />
        <span class="text-sm text-surface-500">{{ form.is_third_party ? $t('common.yes') : $t('common.no') }}</span>
      </div>
    </FormGroup>

    <FormGroup :label="$t('forms.device.device_properties_form.designation')" :error-text="form.errors?.designation" v-if="!form.is_third_party">
      <Dropdown
          v-model="form.designation"
          :options="designationOptions"
          option-label="label"
          option-value="value"
          :disabled="disabled"
          @change="form.validate('designation')"
          data-cy="select-designation-value"
      />
    </FormGroup>

    <FormGroup :label="$t('forms.device.device_properties_form.firmware_last_updated')" :error-text="form.errors?.firmware_last_updated_at">
      <InputText
          disabled
          v-model="form.firmware_last_updated_at"
          :placeholder="$t('common.na')"
          @change="form.validate('firmware_last_updated_at')"/>
    </FormGroup>

    <FormGroup :label="$t('forms.device.device_properties_form.company')" :error-text="form.errors?.company_id"
      data-cy="company-dropdown">
      <Dropdown
		      filter
          v-model="form.company_id"
          :options="companiesStore.getCompanyOptions"
          option-label="name"
          option-value="id"
          :disabled="disabled"
          @change="onCompanyChange"
          show-clear
          data-cy="select-company"
      />
    </FormGroup>

    <FormGroup :label="$t('forms.device.device_properties_form.asset')" :error-text="form.errors?.asset_id" data-cy="click-asset-tab">
        <SelectAssetDropdown
            v-model="form.asset_id"
            :asset-filters="{ company_id: form.company_id }"
        />
    </FormGroup>

    <FormGroup :label="$t('forms.device.device_properties_form.vehicle_manufacturer')" :error-text="form.errors?.vehicle_manufacturer_id"
    data-cy="select-vehicle-manufacturer">
      <Dropdown
          v-model="form.vehicle_manufacturer_id"
          :options="vehicleManufacturers"
          option-label="name"
          option-value="id"
          :disabled="disabled"
          @change="form.validate('vehicle_manufacturer_id')"
          :placeholder="$t('common.na')"
          show-clear
      />
    </FormGroup>

    <FormGroup :label="$t('forms.device.device_properties_form.third_party_device_manufacturer')" :error-text="form.errors?.third_party_device_manufacturer_id" v-if="form.is_third_party">
        <Dropdown
            v-model="form.third_party_device_manufacturer_id"
            :options="deviceManufacturersStore.getDeviceManufacturerOptions"
            option-label="name"
            option-value="id"
            :disabled="disabled"
            @change="onThirdPartyManufacturerChange"
            :placeholder="$t('common.na')"
            show-clear
            data-cy="select-device-manufacturer"
        />
      </FormGroup>

    <FormGroup :label="$t('forms.device.device_properties_form.device_type')" :error-text="form.errors?.device_type_id"
    data-cy="select-device-type">
      <Dropdown
          v-model="form.device_type_id"
          :options="deviceTypesStore.getDeviceTypeOptions"
          option-label="name"
          option-value="id"
          :disabled="disabled"
          @change="onDeviceTypeChange"
      />
    </FormGroup>

    <FormGroup :label="$t('forms.device.device_properties_form.device_model')" :error-text="form.errors.device_model_id"
    data-cy="select-device-model">
      <Dropdown
          v-model="form.device_model_id"
          :options="deviceModels"
          option-label="name"
          option-value="id"
          :disabled="disabled"
          @change="onDeviceModelChange"
      />
    </FormGroup>

    <FormGroup :label="$t('forms.device.device_properties_form.device_revision')" :error-text="form.errors?.device_revision_id"
    data-cy="select-device-revision">
      <Dropdown
          v-model="form.device_revision_id"
          :options="deviceRevisions"
          option-label="name"
          option-value="id"
          :disabled="disabled"
          @change="onDeviceRevisionChange"
      />
    </FormGroup>

    <FormGroup :label="$t('forms.device.device_properties_form.current_firmware')" :error-text="form.errors?.current_firmware_id"
    data-cy="select-current-firmware">
      <Dropdown
          v-model="form.current_firmware_id"
          :options="availableFirmwares"
          option-label="version_number"
          option-value="id"
          :disabled="!form.device_model_id || !form.device_type_id || disabled"
          @change="form.validate('current_firmware_id')"
          data-cy="currentFirmware"
      />
    </FormGroup>

    <FormGroup :label="$t('forms.device.device_properties_form.target_firmware')" :error-text="form.errors?.current_firmware_id"
    data-cy="select-target-firmware">
      <Dropdown
          v-model="form.target_firmware_id"
          :options="availableFirmwares"
          option-label="version_number"
          option-value="id"
          :disabled="!form.device_model_id || !form.device_type_id || disabled"
          @change="form.validate('target_firmware_id')"
          show-clear
          :placeholder="$t('forms.device.device_properties_form.default')"
      />
    </FormGroup>

    <!-- <FormGroup label="R156 Compliant" v-if="!form.is_third_party">
      <InputText
          disabled
          :value="r156CompliantLabel"
      />
    </FormGroup> -->

    <template v-if="form.is_third_party">
      <FormGroup :label="$t('forms.device.device_properties_form.third_party_regulations')" :error-text="form.errors?.regulation_ids">
        <MultiSelect
            v-model="form.regulation_ids"
            :options="regulationsStore.getRegulationOptions"
            option-label="name"
            option-value="id"
            :disabled="disabled"
            :placeholder="$t('common.na')"
            display="chip"
            class="w-full md:w-20rem"
            @change="form.validate('regulation_ids')"
            data-cy="select-regulation-value"
        >
          <template #option="slotProps">
            <div class="flex align-items-center">
              <div>{{ slotProps.option.name }} - <span class="font-light">{{ slotProps.option.description }}</span></div>
            </div>
          </template>
          <template #footer>
            <div class="py-2 px-3">
              {{ $t('forms.common.x_items_selected', form.regulation_ids?.length ?? 0, {count: form.regulation_ids?.length ?? 0}) }}
            </div>
          </template>
        </MultiSelect>
      </FormGroup>

      <FormGroup :label="$t('forms.device.device_properties_form.third_party_notes')" :error-text="form.errors?.third_party_notes">
        <Textarea
            v-model="form.third_party_notes"
            :disabled="disabled"
            @change="form.validate('third_party_notes')"
            rows="4"
        />
      </FormGroup>
    </template>

  </div>
  <template v-if="hasOnSaveListener ||  hasOnDeleteListener || isDeleted">
    <div class="flex justify-end space-x-3 mt-8">
      <Button data-cy="delete-device-admin-button" class="px-8"
              :label="$t('forms.common.delete')"
              severity="danger"
              v-if="hasOnDeleteListener && !disabled && form.id && !isDeleted"
              text
              @click="$emit('on-delete', form)"
              :disabled="form.processing"/>
      <Button class="px-8"
              :label="$t('forms.device.device_properties_form.duplicate')"
              severity="secondary"
              v-if="hasOnDuplicateListener && !disabled && form.id && !isDeleted"
              @click="$emit('on-duplicate', form)"
              :disabled="form.processing"
              data-cy="device-duplicate-btn-properties-form"/>
      <Button class="px-8"
              :label="$t('forms.common.save')"
              v-if="hasOnSaveListener && !disabled && !isDeleted"
              @click="$emit('on-save', form)"
              :disabled="form.processing"
              data-cy="device-save-btn-properties-form"/>
      <Button
              v-if="isDeleted" 
              @click="$emit('on-recover', form)" :label="$t('forms.common.recover')" 
              :disabled="form.processing" severity="warning" />
    </div>
  </template>
</template>

<script>
import {mapStores} from "pinia";
import {useFirmwaresStore} from "@/stores/firmwares.js";
import {useDeviceRevisionsStore} from "@/stores/deviceRevisions.js";
import {useDeviceTypesStore} from "@/stores/deviceTypes.js";
import {useDeviceModelsStore} from "@/stores/deviceModels.js";
import {useCompaniesStore} from "@/stores/companies.js";
import FormGroup from "@/Components/FormGroup.vue";
import {IconReload} from "@tabler/icons-vue";
import SelectAssetDropdown from "@/Components/SelectAssetDropdown.vue";

import {useRegulationsStore} from "@/stores/regulations.js";
import {useDeviceManufacturersStore} from "@/stores/deviceManufacturers.js";

export default {
	name: "DevicePropertiesForm",
	components: {SelectAssetDropdown, IconReload, FormGroup},
	props: {
		disabled: {
			type: Boolean,
			default: false,
		},
		form: {
			type: Object,
			required: true,
		},
		isDeleted: {
			type: Boolean,
			default: false,
		}
  },
	computed: {
		...mapStores(useFirmwaresStore),
		...mapStores(useDeviceRevisionsStore),
		...mapStores(useDeviceTypesStore),
		...mapStores(useDeviceModelsStore),
		...mapStores(useCompaniesStore),
		...mapStores(useRegulationsStore),
		...mapStores(useDeviceManufacturersStore),
		hasOnSaveListener() {
			return !!(this.$attrs?.onOnSave);
		},
		hasOnDeleteListener() {
			return !!(this.$attrs?.onOnDelete);
		},
		hasOnDuplicateListener() {
			return !!(this.$attrs?.onOnDuplicate);
		},
		deviceModels() {
			return this.deviceModelsStore.getDeviceModelOptions?.filter(deviceModel => {
				if (deviceModel.device_type_id !== this.form.device_type_id) {
					return false;
				}

				if (!this.form.is_third_party) {
					return true;
				}

				return this.isNonPublicDeviceModel(deviceModel)
					&& (!this.form.third_party_device_manufacturer_id
						|| deviceModel.device_manufacturer_id === this.form.third_party_device_manufacturer_id);
			}) || [];
		},
		deviceRevisions() {
			return this.deviceRevisionsStore.getDeviceRevisionOptions?.filter(deviceRevision => {
				return deviceRevision.device_model_id === this.form.device_model_id;
			}) || [];
		},
		availableFirmwares() {
			return this.firmwaresStore.getFirmwareOptions?.filter(firmware => {
				return firmware.device_type_id === this.form.device_type_id
            && firmware.device_model_id === this.form.device_model_id
            && firmware.device_revisions.some(revision => revision.id === this.form.device_revision_id);
			}) || [];
		},
		vehicleManufacturers() {
			return this.companiesStore.getCompanyOptions?.filter(company => {
				return !!company.vehicle_manufacturer;
			}) || [];
		},
		designationOptions() {
			return [
				{ label: this.$t('forms.device.device_properties_form.master'), value: 'master' },
				{ label: this.$t('forms.device.device_properties_form.slave'), value: 'slave' },
			];
		},
		r156CompliantLabel() {
			const labels = {
				'is_compliant': this.$t('common.yes'),
				'not_compliant': this.$t('common.no'),
				'not_set': this.$t('forms.device.device_properties_form.not_set')
			};
			return labels[this.form.r156_compliant] || this.$t('forms.device.device_properties_form.not_set');
		},
	},
	methods: {
		onDeviceTypeChange() {
			this.form.device_model_id = null;
			this.onDeviceModelChange();
		},
		onDeviceModelChange() {
			this.form.device_revision_id = null;
			this.onDeviceRevisionChange();
		},
		onDeviceRevisionChange() {
			this.form.current_firmware_id = null;
			this.form.target_firmware_id = null;
			this.form.validate();
		},
		onThirdPartyChange() {
			if (this.form.is_third_party) {
				this.form.device_revision_id = null;
				this.form.current_firmware_id = null;
				this.form.target_firmware_id = null;
				this.form.designation = 'n/a';
				this.clearUnavailableDeviceModel();
				this.form.validate('third_party_device_manufacturer_id');
			} else {
				this.form.regulation_ids = [];
				this.form.third_party_device_manufacturer_id = null;
				this.form.third_party_notes = null;
				this.form.designation = null;
			}
			this.form.validate();
		},
		onCompanyChange() {
      		this.form.asset_id = null;
			this.clearUnavailableDeviceModel();
			this.form.validate();
    	},
		onThirdPartyManufacturerChange() {
			this.clearUnavailableDeviceModel();
			this.form.validate('third_party_device_manufacturer_id');
		},
		isNonPublicDeviceModel(deviceModel) {
			return !!deviceModel.company_id || !!deviceModel.company_ids?.length;
		},
		clearUnavailableDeviceModel() {
			if (!this.form.device_model_id) {
				return;
			}

			if (!this.deviceModels.some(deviceModel => deviceModel.id === this.form.device_model_id)) {
				this.form.device_model_id = null;
				this.onDeviceModelChange();
			}
		},
	},
	mounted() {
		this.firmwaresStore.indexFirmwareOptions();
		this.deviceRevisionsStore.indexDeviceRevisionOptions();
		this.deviceTypesStore.indexDeviceTypeOptions();
		this.deviceModelsStore.indexDeviceModelOptions({}, false);
		this.companiesStore.indexCompanyOptions();
		this.regulationsStore.indexRegulationOptions();
		this.deviceManufacturersStore.indexDeviceManufacturerOptions();
	}
}
</script>

<style scoped>

</style>
